/**
 * Which parts of a downloaded model are the HULL, and which are everything else.
 *
 * WHY THIS EXISTS
 * The camera sits inside the aeroplane. Downloaded models are very often
 * authored `doubleSided` — it hides holes in the mesh and costs the author
 * nothing — and a doubleSided hull viewed from inside is an opaque shell: the
 * pilot sees the inner face of the fuselage instead of the world, and the
 * screen goes dark. So the fix was to force every material on the airframe to
 * FrontSide, which makes the hull transparent from within. Every flight sim
 * relies on that.
 *
 * It was too broad. On this model the cockpit interior is its own 5,098
 * triangle mesh, deliberately authored double-sided because it is a single
 * layer of surfaces rather than a solid — and forcing it single-sided deleted
 * every one of its triangles that happened to face away from the pilot. Sitting
 * still you would never notice, because the parts you are looking at are the
 * parts facing you. Turn your head and pieces of your own aeroplane wink out.
 *
 * The discriminator is SIZE, not material name, not mesh order, and not whether
 * the camera is inside it — the camera is inside the fuselage too. The hull is
 * the thing that spans the whole aeroplane. A cockpit tub, a canopy, an
 * instrument panel, a refuelling probe are all local, and none of them is
 * something you need to see through. So: make the big ones single-sided and
 * leave everything else exactly as the author built it.
 */

/** A mesh this fraction of the whole model's diagonal or more is hull. */
export const HULL_DIAGONAL_FRACTION = 0.5

/**
 * @param {{x:number,y:number,z:number}} size bounding box dimensions
 * @returns {number} the box's diagonal
 */
export function diagonal(size) {
  return Math.hypot(size.x, size.y, size.z)
}

/**
 * Decide, for each part, whether it is hull.
 *
 * @param {Array<{name?:string,size:{x:number,y:number,z:number}}>} parts
 * @param {number} [fraction]
 * @returns {Array<{name?:string,size:object,diagonal:number,share:number,isHull:boolean}>}
 */
export function classifyParts(parts, fraction = HULL_DIAGONAL_FRACTION) {
  const diags = parts.map((p) => diagonal(p.size))
  const biggest = Math.max(...diags, 1e-9)
  return parts.map((p, i) => ({
    ...p,
    diagonal: diags[i],
    share: diags[i] / biggest,
    // Measured against the LARGEST PART, not against the model's overall
    // bounding box. They are usually the same thing and occasionally are not:
    // a model whose wings are separate meshes has an overall box much larger
    // than any single part, and every part then looks small next to it — so
    // nothing gets classified as hull and the pilot is back inside an opaque
    // shell.
    isHull: diags[i] >= biggest * fraction,
  }))
}
